import React, { useEffect, useState } from 'react';

/**
 * DemoWalkthrough — Walter 2026-06-16. Replaces the rejected `/help` doc
 * page: a short, in-app slide walkthrough of how listicity works. Shown
 * automatically on first visit (BEFORE the user starts — "more
 * conventional", Walter's call) and re-openable on demand. Dismissible;
 * never auto-shows twice (localStorage flag).
 *
 * 5 slides (Walter dropped the old "make it yours" slide, kept Share):
 *   what-it-is · type/saves · NL reminders · everywhere · share.
 *
 * Visuals: each slide has a labeled PLACEHOLDER frame for now so the flow
 * + copy are reviewable in context. Real screenshots drop into `slide.img`
 * later — set it to an imported PNG path and the frame swaps to an <img>.
 * Copy is Walter's approved first cut (reviewing in context).
 */

export const DEMO_SEEN_KEY = 'lists_demo_walkthrough_seen_v1';

export function hasSeenDemo() {
  try { return localStorage.getItem(DEMO_SEEN_KEY) === '1'; } catch { return false; }
}
export function markDemoSeen() {
  try { localStorage.setItem(DEMO_SEEN_KEY, '1'); } catch { /* ignore */ }
}

const SLIDES = [
  {
    key: 'canvas',
    caption: 'Your lists and notes, on one canvas.',
    sub: 'No folders, no setup — just start typing.',
    shot: 'a board with a few tiles (a to-do, a notes tile, a reminder list)',
    img: null,
  },
  {
    key: 'saves',
    caption: 'Add a list. It saves itself.',
    sub: 'Everything syncs to your account automatically.',
    shot: 'typing into a tile + the “synced” check up top',
    img: null,
  },
  {
    key: 'reminders',
    caption: 'Type “call mom tomorrow 3pm” — done.',
    sub: 'listicity reads the date and emails you when it’s time.',
    shot: 'a reminder line turning into a 🔔 bell line with the parsed date',
    img: null,
  },
  {
    key: 'everywhere',
    caption: 'Install it. Use it on any device.',
    sub: 'Phone, laptop — the same board, always in sync.',
    shot: 'the install prompt + the same board on a phone frame',
    img: null,
  },
  {
    key: 'share',
    caption: 'Share a list — or an entire board — with anyone.',
    sub: 'They edit, you both stay in sync.',
    shot: 'the share symbol',
    img: null,
  },
  {
    key: 'shortcuts',
    caption: 'Shortcuts for (almost) anything.',
    sub: 'Most things have a keystroke — barely reach for the mouse.',
    shot: 'a few keyboard shortcuts',
    img: null,
  },
];

// ── Illustrative slide art (Walter 2026-06-25) ──────────────────────────────
// Clean on-brand SVG mockups instead of screenshots: they evoke each feature,
// use the theme CSS vars (so they track light/dark + palette) and don't go
// stale when the real UI changes. viewBox 400×190; scales to fill the frame.
//
// The deck is black-themed (dark modal backdrop). ONE exception: the "everywhere"
// slide's devices use a fixed light fill (DEVICE) in both themes — Walter
// 2026-06-25: with theme-var surfaces the laptop/phone vanished into the dark
// backdrop in dark mode. Only those devices go light; every other slide stays
// theme-driven so it reads black-themed.
const DEVICE = {
  body: '#f3f0e8',   // device body / screen
  inner: '#e7e3d8',  // inner mini-tiles
  line: '#cdc8ba',   // borders
};

function Bar({ x, y, w, h = 6, c = 'var(--text-dimmer)', o = 0.4 }) {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={c} opacity={o} />;
}
function Tile({ x, y, w, h, accentTitle, children }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill="var(--surface)" stroke="var(--border)" />
      <rect x={x + 12} y={y + 12} width={Math.min(w - 28, 44)} height={7} rx={3.5}
        fill={accentTitle ? 'var(--accent)' : 'var(--text-dim)'} opacity={accentTitle ? 0.85 : 0.5} />
      {children}
    </g>
  );
}
function Bell({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${s})`} stroke="var(--accent)" strokeWidth="1.4" fill="none">
      <path d="M8 2a3.2 3.2 0 0 0-3.2 3.2c0 2.4-.9 3.6-1.5 4.2-.3.3-.1.8.3.8h8.8c.4 0 .6-.5.3-.8-.6-.6-1.5-1.8-1.5-4.2A3.2 3.2 0 0 0 8 2z" strokeLinejoin="round" />
      <path d="M6.6 12.4a1.5 1.5 0 0 0 2.8 0" strokeLinecap="round" />
    </g>
  );
}
function Frame({ children }) {
  return (
    <svg viewBox="0 0 400 190" width="100%" height="100%" style={{ display: 'block' }} aria-hidden="true">
      {children}
    </svg>
  );
}

function SlideArt({ kind }) {
  switch (kind) {
    case 'canvas':
      return (
        <Frame>
          {/* paragraph-style tile — two short paragraphs */}
          <Tile x={14} y={22} w={112} h={146}>
            {[0, 1].map((p) => (
              <g key={p}>
                {[0, 1, 2].map((l) => <Bar key={l} x={26} y={60 + p * 42 + l * 12} w={l === 2 ? 68 : 90} h={5} />)}
              </g>
            ))}
          </Tile>
          {/* list-style tile — bulleted items */}
          <Tile x={142} y={22} w={112} h={146}>
            {[0, 1, 2, 3].map((k) => (
              <g key={k}>
                <circle cx={156} cy={62 + k * 22} r={2.2} fill="var(--text-dimmer)" opacity={0.6} />
                <Bar x={164} y={59 + k * 22} w={k % 2 ? 60 : 82} h={5} />
              </g>
            ))}
          </Tile>
          {/* reminder-list tile — bell lines */}
          <Tile x={270} y={22} w={116} h={146} accentTitle>
            {[0, 1, 2].map((k) => (
              <g key={k}>
                <Bell x={282} y={56 + k * 32} s={0.8} />
                <Bar x={300} y={61 + k * 32} w={68} h={5} />
              </g>
            ))}
          </Tile>
        </Frame>
      );
    case 'saves':
      // Walter 2026-06-25: just the autosync checkmark — no list tile.
      return (
        <Frame>
          <circle cx={200} cy={88} r={34} fill="var(--accent-wash)" stroke="var(--accent)" strokeWidth="2" />
          <path d="M185 89 l10 11 l21 -25" stroke="var(--accent)" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <text x={200} y={150} fontFamily="var(--mono)" fontSize="13" fill="var(--text-dim)" textAnchor="middle">synced</text>
        </Frame>
      );
    case 'reminders':
      return (
        <Frame>
          <rect x={36} y={32} width={328} height={34} rx={7} fill="var(--surface)" stroke="var(--border)" />
          <text x={52} y={54} fontFamily="var(--mono)" fontSize="13" fill="var(--text)">call mom tomorrow 3pm</text>
          <path d="M200 78 v16 m-5 -5 l5 5 l5 -5" stroke="var(--accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <rect x={36} y={110} width={328} height={38} rx={7} fill="var(--surface)" stroke="var(--border)" />
          <Bell x={50} y={120} />
          <rect x={74} y={120} width={64} height={18} rx={5} fill="var(--accent-wash)" stroke="var(--accent)" opacity={0.95} />
          <text x={82} y={133} fontFamily="var(--mono)" fontSize="11" fill="var(--accent)">Tue 3pm</text>
          <text x={148} y={134} fontFamily="var(--mono)" fontSize="12" fill="var(--text)">: call mom</text>
        </Frame>
      );
    case 'everywhere':
      return (
        <Frame>
          {/* laptop */}
          <rect x={26} y={34} width={172} height={104} rx={8} fill={DEVICE.body} stroke={DEVICE.line} />
          <rect x={40} y={48} width={42} height={76} rx={4} fill={DEVICE.inner} stroke={DEVICE.line} />
          <rect x={92} y={48} width={42} height={50} rx={4} fill={DEVICE.inner} stroke={DEVICE.line} />
          <rect x={144} y={48} width={40} height={76} rx={4} fill={DEVICE.inner} stroke={DEVICE.line} />
          <rect x={14} y={143} width={196} height={7} rx={3.5} fill={DEVICE.line} />
          {/* two-way sync */}
          <g stroke="var(--accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M218 76 h54 m-7 -5 l7 5 l-7 5" />
            <path d="M290 100 h-54 m7 -5 l-7 5 l7 5" />
          </g>
          {/* phone — shorter, with a notch close to the first tile */}
          <rect x={302} y={44} width={70} height={104} rx={13} fill={DEVICE.body} stroke={DEVICE.line} />
          <rect x={326} y={50} width={22} height={5} rx={2.5} fill={DEVICE.line} />
          {[62, 106].map((y, k) => (
            <rect key={k} x={314} y={y} width={46} height={38} rx={5} fill={DEVICE.inner} stroke={DEVICE.line} />
          ))}
        </Frame>
      );
    case 'share':
      // Walter 2026-06-25: just the share symbol, centered + large.
      return (
        <Frame>
          <g transform="translate(200,95)">
            <g transform="scale(3) translate(-7,0)" stroke="var(--accent)" strokeWidth="1.5" fill="none">
              <path d="M2.5 -1.5 l9 -6 M2.5 1.5 l9 6" />
              <circle cx={0} cy={0} r={3.4} fill="var(--accent)" stroke="none" />
              <circle cx={14} cy={-9} r={3.4} fill="var(--accent)" stroke="none" />
              <circle cx={14} cy={9} r={3.4} fill="var(--accent)" stroke="none" />
            </g>
          </g>
        </Frame>
      );
    case 'shortcuts': {
      const rows = [
        { k: 'Ctrl+F', label: 'find' },
        { k: 'Ctrl+H', label: 'highlight' },
        { k: 'Shift+Ctrl+H', label: 'mark done' },
        { k: 'n', label: 'new list' },
        { k: '…', label: 'and lots more', dim: true },
      ];
      return (
        <Frame>
          {rows.map((r, i) => {
            const cy = 28 + i * 34;
            return (
              <g key={r.k} opacity={r.dim ? 0.55 : 1}>
                <rect x={88} y={cy - 12} width={100} height={24} rx={5} fill="var(--surface2)" stroke="var(--border-bright)" />
                <text x={138} y={cy + 4} fontFamily="var(--mono)" fontSize="11" fill="var(--text)" textAnchor="middle">{r.k}</text>
                <text x={202} y={cy + 4} fontFamily="var(--mono)" fontSize="12" fill="var(--text-dim)">{r.label}</text>
              </g>
            );
          })}
        </Frame>
      );
    }
    default:
      return null;
  }
}

export default function DemoWalkthrough({ open, onClose }) {
  const [i, setI] = useState(0);

  // Reset to the first slide each time it opens.
  useEffect(() => { if (open) setI(0); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setI(v => Math.min(v + 1, SLIDES.length - 1));
      else if (e.key === 'ArrowLeft') setI(v => Math.max(v - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const finish = () => { markDemoSeen(); onClose?.(); };
  const last = i === SLIDES.length - 1;
  const slide = SLIDES[i];

  const navBtn = {
    background: 'transparent',
    border: '1px solid var(--border-bright)',
    color: 'var(--text-dim)',
    borderRadius: 'var(--radius)',
    padding: '6px 14px',
    fontFamily: 'var(--mono)',
    fontSize: 'calc(11px * var(--ui-scale, 1))',
    cursor: 'pointer',
  };

  return (
    <div
      onClick={finish}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 2400,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.12s ease',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: 460, width: '100%',
          boxShadow: 'var(--shadow-modal-heavy)',
          fontFamily: 'var(--mono)',
          color: 'var(--text)',
          overflow: 'hidden',
        }}
      >
        {/* skip / close */}
        <button
          type="button"
          onClick={finish}
          style={{
            position: 'absolute', top: 8, right: 10, zIndex: 2,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-dimmer)',
            fontFamily: 'var(--mono)', fontSize: 'calc(10px * var(--ui-scale, 1))',
            padding: 4,
          }}
          title="skip the walkthrough"
        >
          skip ✕
        </button>

        {/* Visual frame — placeholder until real screenshots land. */}
        <div style={{
          height: 200,
          background: 'var(--surface2)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '14px 18px',
        }}>
          {slide.img ? (
            <img
              src={slide.img}
              alt={slide.shot}
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 'var(--radius)' }}
            />
          ) : (
            <SlideArt kind={slide.key} />
          )}
        </div>

        {/* Copy */}
        <div style={{ padding: '18px 22px 14px' }}>
          <div style={{
            fontSize: 'calc(15px * var(--ui-scale, 1))',
            fontWeight: 600,
            lineHeight: 1.4,
            marginBottom: 6,
          }}>
            {slide.caption}
          </div>
          <div style={{
            fontSize: 'calc(12px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
            lineHeight: 1.5,
          }}>
            {slide.sub}
          </div>
        </div>

        {/* Dots + nav */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 22px 18px',
        }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {SLIDES.map((s, idx) => (
              <span
                key={s.key}
                style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: idx === i ? 'var(--accent)' : 'var(--border-bright)',
                  transition: 'background 0.12s',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {i > 0 && (
              <button type="button" style={navBtn} onClick={() => setI(v => Math.max(v - 1, 0))}>
                back
              </button>
            )}
            {!last ? (
              <button
                type="button"
                style={{ ...navBtn, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--bg)', fontWeight: 600 }}
                onClick={() => setI(v => Math.min(v + 1, SLIDES.length - 1))}
              >
                next
              </button>
            ) : (
              <button
                type="button"
                style={{ ...navBtn, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--bg)', fontWeight: 600 }}
                onClick={finish}
              >
                got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
